"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopSubmenu({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className="flex flex-col gap-1 min-w-[200px] p-1">
      {items.map((child, i) => <li key={child.id || `sub-${depth}-${i}`} data-index={i} className="relative group/sub">
          {child.children && child.children.length > 0 ? <div className="flex flex-col">
              <Link to={child.href || "#"} className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-foreground hover:bg-muted transition-colors">
                <span>{child.label}</span>
                <ChevronDown className="size-3.5 -rotate-90 text-muted-foreground" />
              </Link>
              <div className="pl-3 border-l border-border/60 ml-2 mt-1">
                <DesktopSubmenu items={child.children} depth={depth + 1} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={child.href || "#"} className="block px-3 py-2 text-sm font-medium rounded-md text-foreground hover:bg-muted transition-colors">
                {child.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-0.5">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id || `nav-${i}`} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-muted text-foreground font-medium text-sm h-9 px-2">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="bg-popover text-popover-foreground rounded-md border border-border shadow-md p-1.5 min-w-[220px]">
                  <DesktopSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-md px-2 text-sm font-medium text-foreground hover:bg-muted transition-colors">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileSubmenu({
  items,
  depth = 0,
  onItemClick
}: {
  items: MenuItem[];
  depth?: number;
  onItemClick: () => void;
}) {
  return <ul className={`flex flex-col gap-1 ${depth > 0 ? "pl-4 border-l border-border mt-1" : ""}`}>
      {items.map((item, i) => <li key={item.id || `mob-${depth}-${i}`} data-index={i} className="flex flex-col">
          <Link to={item.href || "#"} onClick={onItemClick} className="flex items-center justify-between py-2 px-3 text-base font-medium rounded-md text-foreground hover:bg-muted transition-colors">
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <MobileSubmenu items={item.children} depth={depth + 1} onItemClick={onItemClick} />}
        </li>)}
    </ul>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-6 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-1/2 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-2/3 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 py-2">
      <MobileSubmenu items={menuItems} onItemClick={onClose} />
    </nav>;
}
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if ((window as any).__wvcPhoneClickListenerAttached) return;
    const handlePhoneClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const telLink = target.closest('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!telLink) return;
      if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
        (window as any).gtag("event", "phone_click", {
          page_location: window.location.href,
          link_text: (telLink.textContent || "").trim()
        });
      }
    };
    document.addEventListener("click", handlePhoneClick);
    (window as any).__wvcPhoneClickListenerAttached = true;
    return () => {
      document.removeEventListener("click", handlePhoneClick);
      (window as any).__wvcPhoneClickListenerAttached = false;
    };
  }, []);
  return <section id="header" data-nav="light" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-200 ${scrolled ? "bg-background border-b border-border shadow-xs" : "bg-background/90 backdrop-blur-md border-b border-border/40"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          {/* Brand Logo */}
          <div className="flex items-center shrink-0">
            <Link to="/" className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
              <WvcLogo className="h-[6.75rem] sm:h-24 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1">
            <MenuProvider menu_id="16" className="w-full flex justify-center">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {/* Desktop Right CTA Cluster */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <a href="tel:2135218821" className="inline-flex items-center gap-2 rounded-md bg-secondary text-secondary-foreground font-semibold px-4 py-2 text-sm shadow-xs select-none">
              <span>213-521-8821</span>
            </a>

            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-5 py-2 text-sm shadow-xs">
              <Link to="/ContactRequestAQuotePage">Request a Quote</Link>
            </Button>
          </div>

          {/* Mobile Actions & Drawer Trigger */}
          <div className="flex items-center gap-2 lg:hidden">
            <a href="tel:2135218821" aria-label="Call Fridge Fleet at 213-521-8821" className="md:hidden inline-flex items-center justify-center rounded-md bg-secondary text-secondary-foreground p-2 size-9 shrink-0">
              <Phone className="size-4" />
            </a>

            <SafeHydrate><Drawer open={mobileOpen} onOpenChange={setMobileOpen} direction="right">
              <DrawerTrigger asChild>
                <Button variant="outline" size="icon" className="bg-background border-border text-foreground hover:bg-muted shrink-0" aria-label="Open Navigation Menu">
                  <Menu className="size-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="p-6 flex flex-col h-full bg-background border-l border-border max-w-full sm:max-w-sm">
                <DrawerHeader className="p-0 pb-4 flex flex-row items-center justify-between border-b border-border text-left">
                  <DrawerTitle className="text-left">
                    <Link to="/" onClick={() => setMobileOpen(false)} className="inline-block">
                      <WvcLogo className="h-7 w-auto" />
                    </Link>
                  </DrawerTitle>
                  <DrawerDescription className="sr-only">
                    Primary mobile navigation menu
                  </DrawerDescription>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" className="size-8 rounded-md text-foreground">
                      <X className="size-4" />
                      <span className="sr-only">Close menu</span>
                    </Button>
                  </DrawerClose>
                </DrawerHeader>

                <div className="flex-1 overflow-y-auto py-4">
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="pt-4 mt-auto border-t border-border flex flex-col gap-3">
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11">
                    <Link to="/ContactRequestAQuotePage" onClick={() => setMobileOpen(false)}>
                      Request a Quote
                    </Link>
                  </Button>

                  <a href="tel:2135218821" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-secondary text-secondary-foreground font-semibold h-11 text-sm shadow-xs select-none">
                    <span>213-521-8821</span>
                  </a>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </div>
    </section>;
}